import logger from "./logger";
type Flavor = "corporate" | "individual" | "corporateDev" | "individualDev";

const DEFAULT_SCHEMES = {
  individual: "spacia://",
  corporate: "spacia-corporate://",
  individualDev: "spacia-dev://",
  corporateDev: "spacia-corporate-dev://",
};

// App Store URLs - same as landing-web page
const APP_STORE_IOS_URL = "https://apps.apple.com/gh/app/spacia-work-stay-play/id6739746297";
const APP_STORE_ANDROID_URL = "https://play.google.com/store/apps/details?id=com.lucid_array.spacia.individual&hl=en";

// Domain segments for detection
const APP_PREFIX = "app.";
const ENTERPRISE_PREFIX = "enterprise-app.";
const PRODUCTION_DOMAIN_SUFFIX = ".spacia.africa";
const SANDBOX_DOMAIN_SUFFIX = ".sandbox.spacia.page";

const isProductionEnvironment = (): boolean => {
  // debug: log environment check
  if (typeof window !== "undefined" && window.location) {
    const hostname = window.location.hostname.toLowerCase();

    logger.log("[DEEP_LINK_DEBUG] Checking environment from hostname:", {
      hostname,
      fullUrl: window.location.href,
      containsSandbox: hostname.includes("sandbox"),
    });

    if (hostname.includes("sandbox") || hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
      logger.log("[DEEP_LINK_DEBUG] Detected sandbox/local - using dev flavors");
      return false; // Use dev flavors (corporateDev, individualDev)
    }

    // Production domains: app.spacia.africa or any other production domain
    // Default to production if not sandbox/local
    logger.log("[DEEP_LINK_DEBUG] Detected production - using prod flavors");
    return true; // Use prod flavors (corporate, individual)
  }

  const apiUrl = process.env.REACT_APP_API_URL || "";
  logger.log("[DEEP_LINK_DEBUG] Window not available, checking API URL:", apiUrl);
  if (apiUrl.toLowerCase().includes("sandbox")) {
    return false;
  }

  // Default to production
  return true;
};

/**
 * Get the web origin associated with a specific flavor
 */
export const getWebOriginForFlavor = (flavor?: Flavor): string => {
  const isProduction = isProductionEnvironment();

  // If we are on localhost, stay on localhost
  if (typeof window !== "undefined") {
    const { protocol, hostname, port } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return `${protocol}//${hostname}${port ? `:${port}` : ""}`;
    }

    const isCorporate = (flavor || getAppFlavor()).includes("corporate");

    if (isCorporate) {
      // If we're already on an enterprise domain, keep it
      if (hostname.startsWith("enterprise-app.")) return `${protocol}//${hostname}`;
      // If we're on a standard 'app.' domain, switch to 'enterprise-app.'
      if (hostname.startsWith("app.")) return `${protocol}//${hostname.replace("app.", "enterprise-app.")}`;

      // Fallback fallback based on environment
      return isProductionEnvironment()
        ? `https://enterprise-app.spacia.africa`
        : `https://enterprise-app.sandbox.spacia.page`;
    } else {
      // Standard 'individual' flavor
      // If we're on an enterprise domain, switch to standard
      if (hostname.startsWith("enterprise-app.")) return `${protocol}//${hostname.replace("enterprise-app.", "app.")}`;
      // If we're already on 'app.' or another host, keep it
      if (hostname.startsWith("app.")) return `${protocol}//${hostname}`;

      // Fallback based on environment
      return isProductionEnvironment() ? `https://app.spacia.africa` : `https://app.sandbox.spacia.page`;
    }
  }

  // Server-side or non-window fallback
  return isProductionEnvironment() ? "https://app.spacia.africa" : "https://app.sandbox.spacia.page";
};

/**
 * Get the canonical web domain (always the standard 'app.' one) for the current environment
 */
export const getCanonicalWebDomain = (): string => {
  if (typeof window === "undefined") return isProductionEnvironment() ? "app.spacia.africa" : "app.sandbox.spacia.page";

  const hostname = window.location.hostname.toLowerCase();
  if (hostname === "localhost" || hostname === "127.0.0.1") return hostname;

  if (hostname.startsWith("enterprise-app.")) {
    return hostname.replace("enterprise-app.", "app.");
  }

  return hostname;
};

const getAppFlavor = (): Flavor => {
  const envFlavor = process.env.REACT_APP_FLAVOR;
  const isProduction = isProductionEnvironment();

  // Prefer hostname-based detection if available (e.g. enterprise-app prefix)
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.startsWith("enterprise-")) {
      return isProduction ? "corporate" : "corporateDev";
    }
  }

  if (envFlavor === "corporate") {
    return isProduction ? "corporate" : "corporateDev";
  }
  return isProduction ? "individual" : "individualDev";
};

/**
 * Determine flavor from role code
 * Corporate roles: ROLE_EMPLOYEE, ROLE_APPROVER_EMPLOYEE, ROLE_ADMINISTRATOR, ROLE_SUBSCRIBER_OWNER
 * Individual roles: ROLE_SUBSCRIBER_INDIVIDUAL, ROLE_HOST, ROLE_HOST_OPERATIONS, ROLE_SUBSCRIBER_HOST
 */
export const getFlavorForRole = (roleCode: string | null | undefined): Flavor => {
  if (!roleCode) {
    return getAppFlavor(); // Fallback to app flavor
  }

  const corporateRoles = [
    "ROLE_EMPLOYEE",
    "ROLE_APPROVER_EMPLOYEE",
    "ROLE_ADMINISTRATOR",
    "ROLE_SUBSCRIBER_OWNER",
    "ROLE_HOST_CUSTOMER_OWNER",
    "ROLE_HOST_CUSTOMER_APPROVER_EMPLOYEE",
    "ROLE_HOST_CUSTOMER_EMPLOYEE",
  ];

  const isProduction = isProductionEnvironment();

  if (corporateRoles.includes(roleCode)) {
    return isProduction ? "corporate" : "corporateDev";
  }

  return isProduction ? "individual" : "individualDev";
};

/**
 * Determine flavor from organization type code
 * Corporate org types: CORPORATE_CUSTOMER, PLATFORM_ADMIN, HOST_CORPORATE_CUSTOMER
 * Individual org types: Everything else
 */
export const getFlavorForOrgType = (orgTypeCode: string | null | undefined): Flavor => {
  if (!orgTypeCode) {
    return getAppFlavor(); // Fallback to app flavor
  }

  const corporateOrgTypes = ["CORPORATE_CUSTOMER", "PLATFORM_ADMIN", "HOST_CORPORATE_CUSTOMER"];

  const isProduction = isProductionEnvironment();

  if (corporateOrgTypes.includes(orgTypeCode)) {
    return isProduction ? "corporate" : "corporateDev";
  }

  return isProduction ? "individual" : "individualDev";
};

const getDeepLinkScheme = (flavor?: Flavor): string => {
  const targetFlavor = flavor || getAppFlavor();

  if (targetFlavor === "corporate" || targetFlavor === "corporateDev") {
    return targetFlavor === "corporate" ? DEFAULT_SCHEMES.corporate : DEFAULT_SCHEMES.corporateDev;
  }
  return targetFlavor === "individual" ? DEFAULT_SCHEMES.individual : DEFAULT_SCHEMES.individualDev;
};

/**
 * Detect if device is iOS or Android
 */
export const detectPlatform = (): "ios" | "android" | "unknown" => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return "ios";
  }

  if (/android/.test(userAgent)) {
    return "android";
  }

  return "unknown";
};

/**
 * Get app store URL based on platform
 */
export const getAppStoreUrl = (platform: "ios" | "android" | "unknown" = detectPlatform()): string => {
  if (platform === "ios") {
    return APP_STORE_IOS_URL;
  } else if (platform === "android") {
    return APP_STORE_ANDROID_URL;
  }
  return APP_STORE_ANDROID_URL; // Fallback
};

export const isMobileDevice = (): boolean =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

interface BuildDeepLinkOptions {
  token?: string; // Optional - web already handles confirmation, app just needs to open sign-in
  redirectUrl?: string;
  params?: Record<string, string | number | boolean | undefined>;
  flavor?: Flavor;
  roleCode?: string | null;
  orgTypeCode?: string | null;
  isInvite?: boolean; // Use invite_confirmation action for invites
}

export const buildConfirmAccountDeepLink = ({
  token,
  redirectUrl,
  params,
  flavor,
  roleCode,
  orgTypeCode,
  isInvite = false,
}: BuildDeepLinkOptions): string => {
  // Determine flavor from role/org type if not provided
  let targetFlavor = flavor;
  if (!targetFlavor) {
    if (roleCode) {
      targetFlavor = getFlavorForRole(roleCode);
    } else if (orgTypeCode) {
      targetFlavor = getFlavorForOrgType(orgTypeCode);
    } else {
      targetFlavor = getAppFlavor();
    }
  }

  // debug: log flavor determination
  logger.log("[DEEP_LINK_DEBUG] Building deep link:", {
    roleCode,
    orgTypeCode,
    providedFlavor: flavor,
    determinedFlavor: targetFlavor,
    hostname: typeof window !== "undefined" ? window.location.hostname : "N/A",
    isProduction: isProductionEnvironment(),
  });

  const scheme = getDeepLinkScheme(targetFlavor);
  const url = new URL(scheme);
  const searchParams = url.searchParams;

  // Use correct action based on flow type
  // invite_confirmation for invites, confirm_account for regular confirmations
  const action = isInvite ? "invite_confirmation" : "confirm_account";
  searchParams.set("action", action);
  searchParams.set("flavor", targetFlavor);

  // Token param - use "code" per mobile contract
  if (token) {
    searchParams.set("code", token);
  }

  if (redirectUrl) {
    searchParams.set("redirectUrl", redirectUrl);
  }

  // Add params as JSON string if provided
  if (params && Object.keys(params).length > 0) {
    // Filter out undefined values before stringifying
    const cleanParams: Record<string, string | number | boolean> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value !== "undefined" && value !== null) {
        cleanParams[key] = value;
      }
    });
    if (Object.keys(cleanParams).length > 0) {
      searchParams.set("params", JSON.stringify(cleanParams));
    }
  }

  url.search = searchParams.toString();
  const finalUrl = url.toString();

  // debug: log final deep link url
  logger.log("[DEEP_LINK_DEBUG] Final deep link URL:", finalUrl);

  return finalUrl;
};

export const buildLoginDeepLink = (flavor?: Flavor, roleCode?: string | null, orgTypeCode?: string | null): string => {
  let targetFlavor = flavor;
  if (!targetFlavor) {
    if (roleCode) {
      targetFlavor = getFlavorForRole(roleCode);
    } else if (orgTypeCode) {
      targetFlavor = getFlavorForOrgType(orgTypeCode);
    } else {
      targetFlavor = getAppFlavor();
    }
  }

  const scheme = getDeepLinkScheme(targetFlavor);
  const url = new URL(scheme);
  url.searchParams.set("action", "login");
  return url.toString();
};

interface BuildPaymentRedirectDeepLinkOptions {
  reference?: string;
  status?: string;
  redirectUrl?: string;
  params?: Record<string, string | undefined>;
  flavor?: Flavor;
  roleCode?: string | null;
  orgTypeCode?: string | null;
}

export const buildPaymentRedirectDeepLink = ({
  reference,
  status,
  redirectUrl,
  params,
  flavor,
  roleCode,
  orgTypeCode,
}: BuildPaymentRedirectDeepLinkOptions): string => {
  // Determine flavor from role/org type if not provided
  let targetFlavor = flavor;
  if (!targetFlavor) {
    if (roleCode) {
      targetFlavor = getFlavorForRole(roleCode);
    } else if (orgTypeCode) {
      targetFlavor = getFlavorForOrgType(orgTypeCode);
    } else {
      targetFlavor = getAppFlavor();
    }
  }

  const scheme = getDeepLinkScheme(targetFlavor);
  const url = new URL(scheme);
  const searchParams = url.searchParams;

  searchParams.set("action", "payment_redirect");
  searchParams.set("source", "web_payment");
  searchParams.set("flavor", targetFlavor);

  if (reference) {
    searchParams.set("reference", reference);
  }

  if (status) {
    searchParams.set("status", status);
  }

  if (redirectUrl) {
    searchParams.set("redirectUrl", redirectUrl);
  }

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value !== "undefined" && value !== null) {
        searchParams.set(key, value);
      }
    });
  }

  url.search = searchParams.toString();
  return url.toString();
};

interface OpenDeepLinkOptions {
  timeout?: number;
  onFallback?: () => void;
  redirectToAppStore?: boolean; // Whether to redirect to app store on failure
}

export const openDeepLink = (
  deepLinkUrl: string,
  { timeout = 1400, onFallback, redirectToAppStore = true }: OpenDeepLinkOptions = {}
) => {
  if (!deepLinkUrl) {
    logger.log("[DEEP_LINK_DEBUG] No deep link URL provided");
    return;
  }

  // debug: log attempt to open deep link
  logger.log("[DEEP_LINK_DEBUG] Attempting to open deep link:", {
    url: deepLinkUrl,
    timeout,
    redirectToAppStore,
    initialVisibilityState: document.visibilityState,
    userAgent: navigator.userAgent,
    platform: detectPlatform(),
  });

  let visibilityChanged = false;
  let appOpened = false;

  const handleVisibilityChange = () => {
    visibilityChanged = true;
    logger.log("[DEEP_LINK_DEBUG] Visibility changed:", {
      visibilityState: document.visibilityState,
      timestamp: new Date().toISOString(),
    });
    if (document.visibilityState === "hidden") {
      appOpened = true;
      logger.log("[DEEP_LINK_DEBUG] App opened successfully - page hidden");
      cleanup();
    }
  };

  const redirectToStore = () => {
    logger.log("[DEEP_LINK_DEBUG] Redirecting to app store:", {
      visibilityChanged,
      appOpened,
      redirectToAppStore,
      currentVisibility: document.visibilityState,
    });

    if (redirectToAppStore) {
      const platform = detectPlatform();
      const appStoreUrl = getAppStoreUrl(platform);
      logger.log("[DEEP_LINK_DEBUG] App store URL:", appStoreUrl);
      if (appStoreUrl) {
        window.location.href = appStoreUrl;
        return;
      }
    }
    // If redirectToAppStore is false or no URL found, call custom fallback
    logger.log("[DEEP_LINK_DEBUG] Calling custom fallback");
    onFallback?.();
  };

  const cleanup = () => {
    logger.log("[DEEP_LINK_DEBUG] Cleaning up listeners");
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    if (fallbackTimer) {
      clearTimeout(fallbackTimer);
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);

  const fallbackTimer = window.setTimeout(() => {
    logger.log("[DEEP_LINK_DEBUG] Timeout reached:", {
      timeout,
      visibilityChanged,
      appOpened,
      currentVisibility: document.visibilityState,
      reason: appOpened ? "app opened but cleanup not called" : "app did not open",
    });
    cleanup();
    // If app didn't open (visibility didn't change), redirect to app store
    if (!appOpened) {
      redirectToStore();
    }
  }, timeout);

  try {
    logger.log("[DEEP_LINK_DEBUG] Setting window.location.href to deep link");
    window.location.href = deepLinkUrl;
  } catch (error) {
    logger.error("[DEEP_LINK_DEBUG] Error setting location:", error);
    cleanup();
    redirectToStore();
  }
};
